import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { creatorbaseQueries } from '@round/api';
import { showNotification } from '../../../../../helpers';
import { TableRow } from '../../utils/creatorSearchUtils';
import { useMergeClientInfluencerAccounts } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useSourceAccountImage from './hooks/useSourceAccountImage';
import useSearchAccounts from './hooks/useSearchAccounts';
import { platformLabels, PlatformIcon } from 'Modules/Plans/helpers';
import cn from 'classnames';

import { Dialog, Button, SearchInput, Item, Avatar, Badge, Alert } from '@round/creatorbase-ui';

import styles from './CreatorMergeModal.module.css';

type SourceCreatorData = {
    clientInfluencerId: string;
    fullName: string | null;
    platformUsername: string;
    userId: number;
};

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    sourceCreatorData: SourceCreatorData | null;
    onMergeComplete: () => void;
    sourcePlatform: 'tiktok' | 'instagram';
};

const CreatorMergeModal: React.FC<Props> = ({
    isOpen,
    closeModal,
    sourceCreatorData,
    onMergeComplete,
    sourcePlatform,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTargetCreator, setSelectedTargetCreator] = useState<TableRow | null>(null);
    const { selectedClientId } = useClientSelect();
    const { mutate: merge, isPending: isMerging } = useMergeClientInfluencerAccounts({
        onSuccess: () => {
            showNotification('Creators linked successfully', 'info');
            onMergeComplete();
        },
        onError: (error) => {
            showNotification(creatorbaseQueries.clientInfluencerAccounts.utils.merge.getErrorMessage(error), 'error');
        },
    });

    const targetPlatform = sourcePlatform === 'tiktok' ? 'instagram' : 'tiktok';

    const { image: sourceCreatorImage } = useSourceAccountImage({
        userId: sourceCreatorData?.userId,
        platform: sourcePlatform,
    });

    const validateProfileUrl = useCallback(
        (url: string) => {
            const instagramRegex = /^https?:\/\/(www\.)?instagram\.com\/([a-zA-Z0-9._-]+)\/?$/;
            const tiktokRegex = /^https?:\/\/(www\.)?tiktok\.com\/@([a-zA-Z0-9._-]+)\/?$/;

            const validateTikTokUserProfileUrl = (url: string): { isValid: boolean; username?: string } => {
                const match = url.match(tiktokRegex);
                return {
                    isValid: !!match,
                    username: match ? match[2] : undefined,
                };
            };

            const validateInstagramUserProfileUrl = (url: string): { isValid: boolean; username?: string } => {
                const match = url.match(instagramRegex);
                return {
                    isValid: !!match,
                    username: match ? match[2] : undefined,
                };
            };
            if (sourcePlatform === 'tiktok') {
                return validateInstagramUserProfileUrl(url);
            } else {
                return validateTikTokUserProfileUrl(url);
            }
        },
        [sourcePlatform]
    );

    type UrlPreview = null | { isValid: false } | { isValid: true; url: string; username: string };
    const urlPreview = useMemo<UrlPreview>(() => {
        if (!searchQuery.startsWith('http://') && !searchQuery.startsWith('https://')) {
            return null;
        }
        const { isValid, username } = validateProfileUrl(searchQuery);
        if (isValid && username) {
            return { isValid: true, url: searchQuery, username };
        }
        return { isValid: false };
    }, [searchQuery, validateProfileUrl]);

    const {
        rows: searchResults,
        isFetching: isSearching,
        error: searchError,
    } = useSearchAccounts({
        search: searchQuery,
        targetPlatform,
        clientId: selectedClientId,
        enabled: urlPreview === null,
    });

    useEffect(() => {
        if (searchError) showNotification('Unable to search for creators', 'error');
    }, [searchError]);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setSelectedTargetCreator(null);
        }
    }, [isOpen]);

    const handleMerge = useCallback(() => {
        if (!sourceCreatorData || !selectedTargetCreator) return;
        if (!selectedClientId) {
            showNotification('Please select a client', 'error');
            return;
        }
        const sourceAccountUUID = sourceCreatorData.clientInfluencerId;
        const targetAccountUUID = selectedTargetCreator.creatorbase_client_influencer_id;
        if (!sourceAccountUUID || !targetAccountUUID) {
            showNotification('Unable to find client influencer account IDs linking.', 'error');
            return;
        }
        merge({
            client_id: selectedClientId,
            client_influencer_id_1: String(sourceAccountUUID),
            platform_1: sourcePlatform,
            platform_2: targetPlatform,
            client_influencer_id_2: String(targetAccountUUID),
            mode: 'merge_with_ids',
        });
    }, [sourceCreatorData, selectedTargetCreator, selectedClientId, merge, sourcePlatform, targetPlatform]);

    const handleCreateAndLink = useCallback(() => {
        if (!sourceCreatorData) return;
        if (urlPreview?.isValid !== true) return;

        if (!selectedClientId) {
            showNotification('Please select a client', 'error');
            return;
        }

        const sourceAccountUUID = sourceCreatorData.clientInfluencerId;
        if (!sourceAccountUUID) {
            showNotification('Unable to find client influencer account ID for merging', 'error');
            return;
        }
        merge({
            client_id: selectedClientId,
            platform: sourcePlatform,
            client_influencer_id: sourceAccountUUID,
            url: urlPreview.url,
            mode: 'merge_with_id_and_url',
        });
    }, [sourceCreatorData, urlPreview, selectedClientId, merge, sourcePlatform]);

    const renderIsAlreadyMergedWarning = (selectedTargetCreator: TableRow) => {
        if (
            selectedTargetCreator?.platforms?.tiktok?.length > 0 &&
            selectedTargetCreator?.platforms?.instagram?.length > 0
        ) {
            return (
                <Badge variant="destructive" size="sm">
                    Account already linked
                </Badge>
            );
        }
        return '';
    };

    const renderLinkButton = () => {
        return (
            <Button
                size="sm"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleMerge();
                }}
                disabled={isMerging}
                isLoading={isMerging}
            >
                {isMerging ? 'Linking...' : `Link`}
            </Button>
        );
    };

    if (!sourceCreatorData) return null;

    return (
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <Dialog.Content>
                <Dialog.Header>
                    <Dialog.Title>Link to existing {platformLabels[targetPlatform]} Account</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body className={styles.body}>
                    <div className={styles.section}>
                        <p className={styles.sectionLabel}>Source Account</p>
                        <Item variant="outline" size="sm">
                            <Item.Media>
                                <Avatar className={styles.avatar}>
                                    <Avatar.Image
                                        src={sourceCreatorImage ?? ''}
                                        alt={sourceCreatorData.fullName ?? ''}
                                    />
                                    <Avatar.Fallback>
                                        {sourceCreatorData.fullName?.charAt(0).toUpperCase()}
                                    </Avatar.Fallback>
                                </Avatar>
                            </Item.Media>
                            <Item.Content>
                                <Item.Title>@{sourceCreatorData.fullName}</Item.Title>
                                <Item.Description className={styles.description}>
                                    <PlatformIcon platform={sourcePlatform} className={styles.platformIcon} />
                                    <p>@{sourceCreatorData.platformUsername}</p>
                                </Item.Description>
                            </Item.Content>
                        </Item>
                    </div>
                    <div className={styles.section}>
                        <SearchInput
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`Enter ${platformLabels[targetPlatform]} username or URL...`}
                            debounce={500}
                            aria-invalid={urlPreview?.isValid === false}
                        />
                        {isSearching && <p className={styles.searchingText}>Searching...</p>}
                    </div>

                    <div className={styles.resultsSection}>
                        {urlPreview &&
                            (urlPreview.isValid ? (
                                <Item variant="outline" size="sm" className={styles.selected}>
                                    <Item.Media>
                                        <Avatar className={styles.avatar}>
                                            <Avatar.Fallback>
                                                {urlPreview.username.charAt(0).toUpperCase()}
                                            </Avatar.Fallback>
                                        </Avatar>
                                    </Item.Media>
                                    <Item.Content>
                                        <Item.Title>@{urlPreview.username}</Item.Title>
                                        <Item.Description className={styles.description}>
                                            <PlatformIcon platform={targetPlatform} className={styles.platformIcon} />
                                            {platformLabels[targetPlatform]} Account
                                        </Item.Description>
                                    </Item.Content>
                                    <Item.Actions>
                                        <Button
                                            size="sm"
                                            onClick={handleCreateAndLink}
                                            disabled={isMerging}
                                            isLoading={isMerging}
                                        >
                                            {isMerging
                                                ? 'Creating...'
                                                : `Create & Link ${platformLabels[targetPlatform]} Account`}
                                        </Button>
                                    </Item.Actions>
                                </Item>
                            ) : (
                                <Alert variant="destructive">
                                    <Alert.Title>Invalid URL</Alert.Title>
                                    <Alert.Description>
                                        Please enter a valid {platformLabels[targetPlatform]} URL.
                                        <br />
                                        <span className={styles.muted}>
                                            e.g.{' '}
                                            {targetPlatform === 'instagram'
                                                ? 'https://www.instagram.com/username'
                                                : 'https://www.tiktok.com/@username'}
                                        </span>
                                    </Alert.Description>
                                </Alert>
                            ))}

                        {searchResults.length > 0 && (
                            <div className={styles.resultsList}>
                                {searchResults.map((creator) => {
                                    const isSelected = selectedTargetCreator?._id === creator._id;
                                    const userInitial = creator.full_name?.charAt(0).toUpperCase() ?? '?';
                                    return (
                                        <Item
                                            key={creator._id}
                                            variant="outline"
                                            size="sm"
                                            className={cn(styles.clickable, { [styles.selected]: isSelected })}
                                            onClick={() => setSelectedTargetCreator(creator)}
                                        >
                                            <Item.Media>
                                                <Avatar className={styles.avatar}>
                                                    <Avatar.Image src={creator.userImage ?? ''} alt={userInitial} />
                                                    <Avatar.Fallback>{userInitial}</Avatar.Fallback>
                                                </Avatar>
                                            </Item.Media>
                                            <Item.Content>
                                                <Item.Title>{creator.full_name}</Item.Title>
                                                <Item.Description className={styles.description}>
                                                    <PlatformIcon
                                                        platform={targetPlatform}
                                                        className={styles.platformIcon}
                                                    />
                                                    @{creator.platforms[targetPlatform][0].platform_username}
                                                </Item.Description>
                                                {renderIsAlreadyMergedWarning(creator)}
                                            </Item.Content>
                                            {isSelected && <Item.Actions>{renderLinkButton()}</Item.Actions>}
                                        </Item>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer>
                    <Button variant="outline" onClick={closeModal}>
                        Cancel
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};

export default CreatorMergeModal;
